/**
 * Register.jsx
 * src/components/user/UserRegister.jsx
 *
 * API expected:
 *   POST /users.php?action=register
 *   Body: { name, email, password, mobile, address?, age?, gender? }
 *   Response: { status: true, data: { user_id, name, email, mobile } }
 */

import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useUserAuth } from "../../context/UserAuthContext";
import { apiFetch } from "../helpers";

const GENDERS = ["Male", "Female", "Other"];

export default function Register() {
  const navigate  = useNavigate();
  const { login } = useUserAuth();

  const [step,       setStep]       = useState(1); // 1 = basic info, 2 = contact, 3 = optional details
  const [form,       setForm]       = useState({ 
    name:"", 
    email:"", 
    password:"", 
    confirm_password:"", 
    mobile:"",
    address:"",
    age:"",
    gender:""
  });
  const [error,      setError]      = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showPass,   setShowPass]   = useState(false);

  const validateStep1 = () => {
    if (!form.name.trim())              return setError("Full name is required");
    if (form.name.trim().length < 2)    return setError("Name must be at least 2 characters");
    if (!form.email.trim())             return setError("Email is required");
    if (!/\S+@\S+\.\S+/.test(form.email)) return setError("Invalid email format");
    if (!form.password)                 return setError("Password is required");
    if (form.password.length < 6)       return setError("Password must be at least 6 characters");
    if (form.password !== form.confirm_password) return setError("Passwords do not match");
    return true;
  };

  const validateStep2 = () => {
    if (!form.mobile.trim())        return setError("Mobile number is required");
    if (form.mobile.length < 10)    return setError("Enter a valid 10-digit mobile number");
    return true;
  };

  const handleNext = () => {
    if (validateStep1()) { setError(""); setStep(2); }
  };

  const handleNext2 = () => {
    if (validateStep2()) { setError(""); setStep(3); }
  };

  const handleSubmit = async () => {
    setSubmitting(true); 
    setError("");
    try {
      const json = await apiFetch(`${process.env.REACT_APP_REGISTER_PATIENT}`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    {
          name:             form.name.trim(),
          mobile:           form.mobile.trim(),
          email:            form.email.trim(),
          password:         form.password,
          address:          form.address.trim() || null,
          age:              form.age ? parseInt(form.age) : null,
          gender:           form.gender || null,
        },
      });
      
      if (json.status) {
        navigate("/login", { replace: true });
      } else {
        setError(json.message || "Registration failed. Please try again.");
      }
    } catch(err) {
      console.log(err); 
      setError("Network error. Please try again."); 
    }
    setSubmitting(false);
  };

  const inputStyle = {
    width:"100%", padding:"11px 14px", borderRadius:10,
    border:"1.5px solid #E4EAF0", fontSize:14, outline:"none",
    transition:"border .2s", background:"#F8FAFC", color:"#0A1628",
    fontFamily:"inherit",
  };
  const onFocus = e => { e.target.style.borderColor = "#1D9E75"; e.target.style.background = "#fff"; };
  const onBlur  = e => { e.target.style.borderColor = "#E4EAF0"; e.target.style.background = "#F8FAFC"; };

  return (
    <div style={{
      minHeight:"100vh",
      background:"linear-gradient(135deg, #0A1628 0%, #1B2A6B 55%, #0A1628 100%)",
      display:"flex", alignItems:"center", justifyContent:"center",
      fontFamily:"'Nunito','Segoe UI',sans-serif", padding:"1rem",
      position:"relative", overflow:"hidden",
    }}>

      {/* BG decorations */}
      <div style={{ position:"absolute", inset:0, pointerEvents:"none", overflow:"hidden" }}>
        <div style={{ position:"absolute", width:350, height:350, borderRadius:"50%", background:"rgba(29,158,117,.08)", top:-80, right:-60 }} />
        <div style={{ position:"absolute", width:220, height:220, borderRadius:"50%", background:"rgba(232,50,60,.05)", bottom:-50, left:-40 }} />
      </div>

      <div style={{
        background:"rgba(255,255,255,.97)", borderRadius:24, padding:"40px 44px",
        width:"100%", maxWidth:460, boxShadow:"0 32px 80px rgba(0,0,0,0.35)",
        position:"relative", zIndex:1,
      }}>

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:24 }}>
          <div style={{ width:56, height:56, borderRadius:16, background:"rgba(29,158,117,.1)", border:"2px solid rgba(29,158,117,.3)", display:"inline-flex", alignItems:"center", justifyContent:"center", marginBottom:12 }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <path d="M12 2C9 2 6.5 4.2 6.5 7C6.5 8.6 7.1 9.7 7.1 11.2C7.1 13.8 6 16.2 6 18.6C6 20.6 7 22 8.5 22C10 22 10.5 20 12 20C13.5 20 14 22 15.5 22C17 22 18 20.6 18 18.6C18 16.2 16.9 13.8 16.9 11.2C16.9 9.7 17.5 8.6 17.5 7C17.5 4.2 15 2 12 2Z" fill="#1D9E75"/>
              <path d="M9.5 7.5C9.5 6 10.6 5 12 5C13.4 5 14.5 6 14.5 7.5" stroke="white" strokeWidth="1.4" strokeLinecap="round"/>
            </svg>
          </div>
          <h1 style={{ fontSize:22, fontWeight:800, color:"#0A1628", margin:"0 0 4px", letterSpacing:"-0.3px" }}>Create Account</h1>
          <p style={{ fontSize:13, color:"#6B7A8D", margin:0 }}>Join BrightSmile Dental — it's free!</p>
        </div>

        {/* Step indicators */}
        <div style={{ display:"flex", gap:8, marginBottom:24 }}>
          {[1,2,3].map(n => (
            <div key={n} style={{
              flex:1, height:5, borderRadius:4,
              background: step >= n ? "#1D9E75" : "#E4EAF0",
              transition:"background .3s",
            }} />
          ))}
        </div>
        <p style={{ fontSize:12, color:"#6B7A8D", marginBottom:20, fontWeight:600 }}>
          Step {step} of 3 — {step===1 ? "Account Information" : step===2 ? "Contact" : "Additional Details"}
        </p>

        {/* Error */}
        {error && (
          <div style={{ background:"#FCEBEB", border:"1px solid #F09595", borderRadius:10, padding:"10px 14px", marginBottom:18, fontSize:13, color:"#501313", display:"flex", gap:8, alignItems:"center" }}>
            <span>⚠️</span> {error}
          </div>
        )}

        {/* ── STEP 1 ── */}
        {step === 1 && (
          <div>
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:6 }}>FULL NAME *</label>
              <input type="text" value={form.name} onChange={e => setForm({ ...form, name:e.target.value })} placeholder="e.g. Priya Sharma" style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:6 }}>EMAIL ADDRESS *</label>
              <input type="email" value={form.email} onChange={e => setForm({ ...form, email:e.target.value })} placeholder="Enter your email" style={inputStyle} onFocus={onFocus} onBlur={onBlur} />
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:6 }}>PASSWORD *</label>
              <div style={{ position:"relative" }}>
                <input
                  type={showPass ? "text" : "password"}
                  value={form.password}
                  onChange={e => setForm({ ...form, password:e.target.value })}
                  placeholder="Min 6 characters"
                  style={{ ...inputStyle, paddingRight:44 }}
                  onFocus={onFocus} onBlur={onBlur}
                />
                <button type="button" onClick={() => setShowPass(!showPass)}
                  style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", fontSize:16, color:"#6B7A8D" }}>
                  {showPass ? "🙈" : "👁️"}
                </button>
              </div>
              {/* Password strength bar */}
              {form.password && (
                <div style={{ marginTop:6, height:3, borderRadius:2, background:"#E4EAF0" }}>
                  <div style={{ height:"100%", borderRadius:2, transition:"width .3s, background .3s",
                    width: form.password.length < 4 ? "25%" : form.password.length < 6 ? "50%" : form.password.length < 10 ? "75%" : "100%",
                    background: form.password.length < 4 ? "#E24B4A" : form.password.length < 6 ? "#BA7517" : form.password.length < 10 ? "#378ADD" : "#1D9E75",
                  }} />
                </div>
              )}
            </div>

            <div style={{ marginBottom:24 }}>
              <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:6 }}>CONFIRM PASSWORD *</label>
              <input
                type="password"
                value={form.confirm_password}
                onChange={e => setForm({ ...form, confirm_password:e.target.value })}
                placeholder="Repeat your password"
                style={{
                  ...inputStyle,
                  borderColor: form.confirm_password && form.confirm_password !== form.password ? "#E24B4A" : "#E4EAF0",
                }}
                onFocus={onFocus} onBlur={onBlur}
              />
              {form.confirm_password && form.confirm_password !== form.password && (
                <p style={{ fontSize:11, color:"#E24B4A", margin:"4px 0 0" }}>Passwords do not match</p>
              )}
            </div>

            <button onClick={handleNext} style={{ width:"100%", padding:"13px", background:"#1D9E75", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, cursor:"pointer", transition:"all .2s" }}
              onMouseEnter={e => e.currentTarget.style.background = "#0F6E56"}
              onMouseLeave={e => e.currentTarget.style.background = "#1D9E75"}>
              Continue →
            </button>
          </div>
        )}

        {/* ── STEP 2 ── */}
        {step === 2 && (
          <div>
            <div style={{ marginBottom:24 }}>
              <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:6 }}>MOBILE NUMBER *</label>
              <div style={{ display:"flex", gap:0 }}>
                <span style={{ padding:"11px 12px", background:"#F0F4F8", border:"1.5px solid #E4EAF0", borderRadius:"10px 0 0 10px", fontSize:14, color:"#0A1628", display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
                  🇮🇳 +91
                </span>
                <input
                  type="tel"
                  value={form.mobile}
                  onChange={e => setForm({ ...form, mobile:e.target.value.replace(/\D/,"").slice(0,10) })}
                  placeholder="10-digit mobile number"
                  style={{ ...inputStyle, borderRadius:"0 10px 10px 0", borderLeft:"none", flex:1 }}
                  onFocus={onFocus} onBlur={onBlur}
                />
              </div>
            </div>

            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => { setStep(1); setError(""); }} style={{ flex:1, padding:"13px", background:"#F0F4F8", color:"#0A1628", border:"none", borderRadius:10, fontSize:14, fontWeight:600, cursor:"pointer", transition:"all .2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#E4EAF0"}
                onMouseLeave={e => e.currentTarget.style.background = "#F0F4F8"}>
                ← Back
              </button>
              <button onClick={handleNext2} style={{ flex:2, padding:"13px", background:"#1D9E75", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, cursor:"pointer", transition:"all .2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#0F6E56"}
                onMouseLeave={e => e.currentTarget.style.background = "#1D9E75"}>
                Continue →
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3 (Optional Details) ── */}
        {step === 3 && (
          <div>
            <div style={{ marginBottom:14, padding:12, background:"rgba(29,158,117,.08)", border:"1px solid rgba(29,158,117,.15)", borderRadius:8 }}>
              <p style={{ fontSize:12, color:"#085041", margin:0, fontWeight:600 }}>ℹ️ The following fields are optional</p>
            </div>

            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:6 }}>ADDRESS</label>
              <input 
                type="text" 
                value={form.address} 
                onChange={e => setForm({ ...form, address:e.target.value })} 
                placeholder="e.g. 123 Main Street, City" 
                style={inputStyle} 
                onFocus={onFocus} 
                onBlur={onBlur} 
              />
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:24 }}>
              <div>
                <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:6 }}>AGE</label>
                <input 
                  type="number" 
                  value={form.age} 
                  onChange={e => setForm({ ...form, age:e.target.value })} 
                  placeholder="e.g. 25" 
                  min="1"
                  max="120"
                  style={inputStyle} 
                  onFocus={onFocus} 
                  onBlur={onBlur} 
                />
              </div>
              <div>
                <label style={{ fontSize:12, fontWeight:700, color:"#0A1628", display:"block", marginBottom:6 }}>GENDER</label>
                <select 
                  value={form.gender} 
                  onChange={e => setForm({ ...form, gender:e.target.value })}
                  style={{ ...inputStyle, appearance:"none", cursor:"pointer" }}
                  onFocus={onFocus} 
                  onBlur={onBlur}>
                  <option value="">Select Gender</option>
                  {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                </select>
              </div>
            </div>

            <div style={{ display:"flex", gap:10 }}>
              <button onClick={() => { setStep(2); setError(""); }} style={{ flex:1, padding:"13px", background:"#F0F4F8", color:"#0A1628", border:"none", borderRadius:10, fontSize:14, fontWeight:600, cursor:"pointer", transition:"all .2s" }}
                onMouseEnter={e => e.currentTarget.style.background = "#E4EAF0"}
                onMouseLeave={e => e.currentTarget.style.background = "#F0F4F8"}>
                ← Back
              </button>
              <button onClick={handleSubmit} disabled={submitting}
                style={{ flex:2, padding:"13px", background:"#E8323C", color:"#fff", border:"none", borderRadius:10, fontSize:15, fontWeight:700, cursor: submitting ? "not-allowed" : "pointer", opacity: submitting ? .7 : 1, transition:"all .2s", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}
                onMouseEnter={e => { if (!submitting) e.currentTarget.style.background = "#C82030"; }}
                onMouseLeave={e => e.currentTarget.style.background = "#E8323C"}>
                {submitting ? <><span className="spinner-border spinner-border-sm me-1" />Creating...</> : "Create Account 🎉"}
              </button>
            </div>
          </div>
        )}

        <div style={{ textAlign:"center", marginTop:20 }}>
          <span style={{ fontSize:13, color:"#6B7A8D" }}>Already have an account? </span>
          <Link to="/login" style={{ fontSize:13, color:"#1D9E75", fontWeight:700, textDecoration:"none" }}>Sign In</Link>
        </div>
        <div style={{ textAlign:"center", marginTop:8 }}>
          <Link to="/" style={{ fontSize:12, color:"#6B7A8D", textDecoration:"none" }}>← Back to Home</Link>
        </div>
      </div>
    </div>
  );
}